import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Mail, Lock, Eye, EyeOff, Phone, MapPin } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/api/customer-portal/login`, {
        email,
        password
      });

      if (response.data.success) {
        localStorage.setItem('customer_token', response.data.access_token);
        localStorage.setItem('customer_data', JSON.stringify(response.data.customer));
        
        toast.success(`Welcome back, ${response.data.customer.first_name}!`);
        navigate('/customer-portal');
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.detail || 'Invalid email or password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1f2e] flex flex-col">
      {/* Header - Matching Rhino Shield website style */}
      <header className="bg-[#0f1219] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#2dd4bf] to-[#14b8a6] rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/20">
                <Shield className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-xl text-white tracking-tight">Rhino Shield</h1>
                <p className="text-xs text-[#2dd4bf]">Florida</p>
              </div>
            </div>
            <a 
              href="tel:9042993233" 
              className="hidden sm:flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm">(904) 299-3233</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Gradient Orbs */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-teal-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />

        <Card className="w-full max-w-md relative z-10 bg-[#0f1219]/90 backdrop-blur-xl border-white/10 shadow-2xl">
          <CardHeader className="text-center space-y-4 pb-2">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-[#2dd4bf] to-[#0d9488] rounded-2xl flex items-center justify-center shadow-lg shadow-teal-500/30">
              <Shield className="h-10 w-10 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-white">Customer Portal</CardTitle>
              <CardDescription className="text-white/60 mt-2">
                Access your project details, documents, and payments
              </CardDescription>
            </div>
          </CardHeader>
          
          <CardContent className="pt-6">
            <form onSubmit={handleLogin} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#2dd4bf] focus:ring-[#2dd4bf]/20"
                    required
                    data-testid="customer-login-email"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-white/80">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-[#2dd4bf] focus:ring-[#2dd4bf]/20"
                    required
                    data-testid="customer-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#2dd4bf] to-[#0d9488] hover:from-[#14b8a6] hover:to-[#0f766e] text-white font-semibold py-5 shadow-lg shadow-teal-500/20 transition-all duration-200"
                disabled={loading}
                data-testid="customer-login-submit"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </div>
                ) : 'Sign In to Portal'}
              </Button>
            </form>
            
            <div className="mt-8 text-center">
              <p className="text-sm text-white/50">
                Not a customer yet?{' '}
                <a 
                  href="https://rhinoshieldflorida.com/request-a-quote/" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#2dd4bf] hover:text-[#5eead4] transition-colors font-medium"
                >
                  Get a free estimate
                </a>
              </p>
            </div>
            
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-xs text-center text-white/40">
                Employee?{' '}
                <a href="/login" className="text-[#2dd4bf] hover:text-[#5eead4] transition-colors">
                  Sign in to Rhino-1
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="bg-[#0f1219] border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-white/40 text-sm">
              <MapPin className="h-4 w-4" />
              <span>Serving all of Florida</span>
            </div>
            <p className="text-white/40 text-sm">
              Rhino Shield Florida - Ceramic Coating Experts - 25 Year Warranty
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
